import React, { useState, useCallback } from "react"
import { Tabs } from "antd"

import GeneralInformationTab from "features/ProjectDetails/components/GeneralInformationTab.component"
import QuestionnaireEditorContainer from "features/QuestionnaireEditor/containers/QuestionnaireEditor.container"
import QueryConsoleContainer from "features/QueryConsole/containers/QueryConsole.container"
import DocumentsContainer from "features/Documents/containers/Documents.container"
import DataImportContainer from "features/DataImport/containers/DataImport.container"
import { TabItem } from "types/ui.types"
import { PROJECT_TYPE } from "types/project.types"

// Define the props interface
interface ProjectDetailsTabsProps {
  projectType: PROJECT_TYPE
  refreshProject: () => void
}

/**
 * ProjectDetailsTabs component - Displays tabbed navigation for different sections of project details.
 *
 * This component leverages Ant Design's Tabs component to offer a tabbed interface, facilitating
 * the display of various sections of a project's details. Currently, it includes tabs for
 * "General Information" and "Questionnaire". Each tab is responsible for rendering a specific
 * component related to the project's details. The "General Information" tab renders the
 * GeneralInformationTab component, passing down the `refreshProject` function and the `project`
 * object as props to allow for dynamic updates. The structure is designed to be easily extendable
 * with additional tabs as needed.
 *
 * @component
 * @param props - The component props.
 * @returns The ProjectDetailsTabs component, rendering the tabbed interface.
 */
const ProjectDetailsTabs: React.FC<ProjectDetailsTabsProps> = ({
  projectType,
  refreshProject,
}) => {
  const [activeKey, setActiveKey] = useState<string>("1")

  const allItems: TabItem[] = [
    {
      key: "1",
      label: "General Information",
      children: <GeneralInformationTab refreshProject={refreshProject} />,
    },
    {
      key: "2",
      label: "Questionnaire",
      children: activeKey === "2" ? <QuestionnaireEditorContainer /> : null,
    },
    {
      key: "3",
      label: "Documents",
      children: <DocumentsContainer />,
    },
    {
      key: "4",
      label: "Data Import",
      children: <DataImportContainer />,
    },
    {
      key: "5",
      label: "Data Reporting",
      // No remount key here: antd mounts this pane on first activation and
      // keeps it mounted. Remounting on every tab visit (the old
      // `reporting-${reportingTabKey}` key) created a fresh engine query
      // instance — a full copy of the dataset — per visit.
      children: <QueryConsoleContainer activeKey={activeKey} />,
    },
  ]

  // Filter out Questionnaire tab if project type is data-only
  const items: TabItem[] = allItems.filter((item) => {
    if (projectType === PROJECT_TYPE.DATA_ONLY && item.key === "2") {
      return false
    }
    return true
  })

  // Use useCallback to ensure stable reference
  const handleTabChange = useCallback((key: string) => {
    setActiveKey(key)
  }, [])

  return (
    <Tabs
      size="large"
      type="card"
      items={items}
      activeKey={activeKey}
      onChange={handleTabChange}
      className="project-details-tabs bg-white rounded-2xl h-full"
    />
  )
}

export default ProjectDetailsTabs
